"use client"

import { useState, useRef, useCallback } from "react"
import { Upload, FileText, X, File, FolderOpen } from "lucide-react"
import { Button } from "@/components/ui/Button"
import { Spinner } from "@/components/ui/spinner"
import { useDemoDealsDocuments } from "@/hooks/deals/useDemoDealsDocuments"
import { useIsDemo } from "@/hooks/common/useIsDemo"
import { useOrg } from "@/contexts/OrgContext"
import { useFileOperations } from "@/hooks/files/useFileOperations"
import { ContractTypeIcon } from "src/components/ContractTypeIcon"
import { cn } from "src/utils/Functions"
import { Tabs, TabsList, TabsTab, TabsPanel } from "@/components/ui/tabs"


interface DocumentSelectorProps {
  files: File[]
  selectedContracts: string[]
  onFilesChange: (files: File[]) => void
  onContractsChange: (contracts: string[]) => void
  compact?: boolean
  className?: string
}

const ACCEPTED_FILE_TYPES = {
  "application/pdf": [".pdf"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
}

const ACCEPTED_EXTENSIONS = [".pdf", ".doc", ".docx"]

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes"
  const k = 1024
  const sizes = ["Bytes", "KB", "MB", "GB"]
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
}

function getFileIcon(file: File) {
  if (file.type === "application/pdf") {
    return <FileText className="size-5 text-red-400" />
  }
  return <File className="size-5 text-blue-400" />
}

function getVaultFileIcon(fileName: string) {
  const ext = fileName.split(".").pop()?.toLowerCase()
  if (ext === "pdf") return <FileText className="size-5 text-red-400" />
  if (ext === "doc" || ext === "docx") return <File className="size-5 text-blue-400" />
  if (ext === "xlsx" || ext === "xls") return <File className="size-5 text-green-400" />
  return <File className="size-5 text-neutral-400" />
}

function getExtLabel(fileName: string): string {
  const ext = fileName.split(".").pop()?.toLowerCase()
  if (ext === "pdf") return "PDF"
  if (ext === "doc" || ext === "docx") return "Word Document"
  if (ext === "xlsx" || ext === "xls") return "Spreadsheet"
  return ext?.toUpperCase() || "File"
}

interface VaultItem {
  id: string
  name: string
  subtitle: string
}

export function DocumentSelector({
  files,
  selectedContracts,
  onFilesChange,
  onContractsChange,
  compact = false,
  className,
}: DocumentSelectorProps) {
  const isDemo = useIsDemo()
  const { selectedOrgId } = useOrg()
  const { demoContracts } = useDemoDealsDocuments()
  const { files: apiFiles, isLoading: vaultLoading } = useFileOperations(
    isDemo ? undefined : selectedOrgId,
  )
  const [activeTab, setActiveTab] = useState<string | number | null>("upload")
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const vaultItems: VaultItem[] = isDemo
    ? demoContracts.map((c) => ({ id: c.contractId, name: c.name, subtitle: c.type }))
    : apiFiles.map((f) => ({ id: f.fileId, name: f.fileName, subtitle: getExtLabel(f.fileName) }))

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const isValidFile = (file: File): boolean => {
    const extension = "." + file.name.split(".").pop()?.toLowerCase()
    return ACCEPTED_EXTENSIONS.includes(extension)
  }

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const droppedFiles = Array.from(e.dataTransfer.files).filter(isValidFile)
    if (droppedFiles.length > 0) {
      onFilesChange([...files, ...droppedFiles])
    }
  }, [files, onFilesChange])

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []).filter(isValidFile)
    if (selectedFiles.length > 0) {
      onFilesChange([...files, ...selectedFiles])
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const removeFile = (index: number) => {
    onFilesChange(files.filter((_, i) => i !== index))
  }

  const toggleContractSelection = (contractId: string) => {
    if (selectedContracts.includes(contractId)) {
      onContractsChange(selectedContracts.filter(id => id !== contractId))
    } else {
      onContractsChange([...selectedContracts, contractId])
    }
  }

  return (
    <div className={className}>
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList variant="default" className="grid w-full grid-cols-2 bg-white/5">
          <TabsTab value="upload">
            <Upload className="size-4 mr-2" />
            Upload Files
          </TabsTab>
          <TabsTab value="vault">
            <FolderOpen className="size-4 mr-2" />
            From Vault
          </TabsTab>
        </TabsList>

        {/* Upload Tab */}
        <TabsPanel value="upload" className="mt-4 space-y-4">
          {/* Drop Zone */}
          <div
            className={cn(
              "relative rounded-xl border-2 border-dashed transition-colors",
              compact ? "p-8" : "p-16",
              isDragging
                ? "border-primary bg-primary/5"
                : "border-muted-foreground/25 hover:border-muted-foreground/50"
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx"
              multiple
              onChange={handleFileSelect}
              className="sr-only"
              id="file-upload"
            />
            <label
              htmlFor="file-upload"
              className="flex flex-col items-center justify-center gap-2 cursor-pointer"
            >
              <div
                className={cn(
                  "rounded-full p-3 transition-colors",
                  isDragging ? "bg-primary/10" : "bg-muted"
                )}
              >
                <Upload
                  className={cn(
                    compact ? "size-8" : "size-12",
                    isDragging ? "text-primary" : "text-muted-foreground"
                  )}
                />
              </div>
              <div className="text-center">
                <p className={cn("font-medium", compact ? "text-sm" : "text-base")}>
                  {isDragging ? "Drop files here" : "Click to upload or drag and drop"}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  PDF, DOC, or DOCX
                </p>
              </div>
            </label>
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Files to upload ({files.length})
              </label>
              <div className="space-y-2 max-h-40 overflow-auto">
                {files.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="flex items-center gap-3 p-3 rounded-lg bg-muted/50"
                  >
                    {getFileIcon(file)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      onClick={() => removeFile(index)}
                      aria-label={`Remove ${file.name}`}
                    >
                      <X className="size-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsPanel>

        {/* From Vault Tab */}
        <TabsPanel value="vault" className="mt-4 space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Select from your vault ({selectedContracts.length} selected)
            </label>
            <div className="space-y-2 max-h-64 overflow-auto border border-muted rounded-lg p-2">
              {!isDemo && vaultLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Spinner className="size-5" />
                </div>
              ) : vaultItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                  <FolderOpen className="size-8 mb-2 opacity-50" />
                  <p className="text-sm">No files in your vault yet</p>
                </div>
              ) : (
                vaultItems.map((item) => {
                  const isSelected = selectedContracts.includes(item.id)
                  return (
                    <button
                      key={item.id}
                      onClick={() => toggleContractSelection(item.id)}
                      className={cn(
                        "w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left",
                        isSelected
                          ? "bg-primary/10 border-2 border-primary"
                          : "bg-muted/30 hover:bg-muted/50 border-2 border-transparent"
                      )}
                    >
                      <div className="size-8 flex items-center justify-center rounded-lg bg-background shrink-0">
                        {isDemo ? (
                          <ContractTypeIcon type={item.subtitle} />
                        ) : (
                          getVaultFileIcon(item.name)
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.name}</p>
                        <p className="text-xs text-muted-foreground capitalize">
                          {item.subtitle}
                        </p>
                      </div>
                      {isSelected && (
                        <div className="size-5 bg-primary rounded-full flex items-center justify-center shrink-0">
                          <FileText className="size-3 text-primary-foreground" />
                        </div>
                      )}
                    </button>
                  )
                })
              )}
            </div>
          </div>
        </TabsPanel>
      </Tabs>
    </div>
  )
}
